import type { Competitor } from '../types'

// Sources (checked 2026-10-01): linktr.ee/s/pricing (USD; Linktree shows
// regional prices, EUR in the Netherlands), Linktree help center articles on
// fees, themes, fonts, backgrounds, custom domains, and the app marketplace.
const linktree: Competitor = {
  slug: 'linktree',
  name: 'Linktree',
  tagline: 'The best-known link-in-bio tool, strong on selling and marketing apps.',
  site: 'https://linktr.ee',
  pricingUrl: 'https://linktr.ee/s/pricing',
  checkedOn: '2026-10-01',
  seo: {
    title: 'Linktree alternative: mychar.gg vs Linktree compared (2026)',
    description: 'A Linktree alternative you design yourself. Compare mychar.gg and Linktree on design, music, video clips, custom domains, analytics, and price.'
  },
  hero: {
    title: 'The Linktree alternative you design yourself',
    description: 'Keep the one-link simplicity and lose the template look. Build your page in a live drag-and-drop editor, add music and clips, and pay once instead of every month.'
  },
  summary: 'mychar.gg is a Linktree alternative for anyone who wants the page itself to stand out: you design it in a live editor with animated backgrounds, custom cursors, a music player, and video clips, and you can put it on your own domain, which Linktree does not offer. Removing branding costs €6.99 once on mychar.gg versus $12 a month on Linktree Pro. Linktree is still the stronger choice if you mainly sell products or rely on its marketing apps.',
  chooseMychar: [
    'want a page that looks designed, not a list of buttons',
    'want music and video clips to play on the page itself',
    'want your own domain on your link in bio',
    'would rather pay once than every month'
  ],
  chooseThem: [
    'sell digital products or courses from your bio',
    'need email signups, link scheduling, or Instagram auto-replies',
    'want the name your audience already recognizes'
  ],
  features: {
    free_plan: { yes: true, text: 'Unlimited links, with Linktree branding' },
    paid_plans: 'Starter $6, Pro $12, Premium $30 a month',
    billing: 'Monthly or yearly subscription',
    first_year: '$144 (Pro, billed yearly)',
    custom_domain: { yes: false, text: 'Not offered' },
    remove_branding: { yes: true, text: 'Pro and up' },
    editor: 'A list of links next to a phone preview',
    drag_drop: { yes: true, text: 'Reorder links' },
    themes: { yes: true, text: 'Linktree themes, more on Pro' },
    fonts: 'Font picker, more pairings on Pro',
    custom_fonts: false,
    effects: false,
    cursors: false,
    video_background: { yes: true, text: 'Pro and up, muted loop' },
    custom_code: false,
    links: { yes: true, text: 'Unlimited, any URL' },
    music_player: { yes: false, text: 'Spotify and SoundCloud embeds' },
    video_clips: { yes: false, text: 'Embeds from other sites' },
    comments: false,
    view_counter: false,
    badges: 'Verification badge on Starter',
    qr_code: true,
    youtube: 'Video embeds, latest video on Pro',
    twitch: 'Stream and chat embeds',
    discord: 'Server link app',
    music_embeds: { yes: true, text: 'Spotify, SoundCloud, and more' },
    analytics_free: { yes: true, text: 'Last 28 days' },
    analytics_history: '28 days, up to all-time on Premium',
    analytics_live: 'Location and referrers on Pro',
    store: { yes: true, text: 'Every plan, 12% fee on Free' },
    email_capture: { yes: true, text: 'Every plan' },
    scheduling: { yes: true, text: 'Starter and up' },
    mobile_app: { yes: true, text: 'iOS and Android' }
  },
  plans: [
    { name: 'Free', price: '$0', period: 'forever', note: 'Unlimited links and email signups, Linktree footer, 12% fee on product sales.' },
    { name: 'Starter', price: '$6', period: '/month, billed yearly', note: 'Or $8 monthly. Scheduling, redirects, 90 days of analytics.' },
    { name: 'Pro', price: '$12', period: '/month, billed yearly', note: 'Or $15 monthly. Removes branding, video backgrounds, mailing list sync.' },
    { name: 'Premium', price: '$30', period: '/month, billed yearly', note: 'Or $35 monthly. 0% product fees, all-time analytics, link rules.' }
  ],
  plansNote: 'USD. Linktree shows regional prices, so you may see a different amount or currency. Card fees from the payment provider apply on top of product fees.',
  advantages: [
    {
      title: 'Your own domain',
      description: 'Connect a domain you own for €2.99 per month on any plan. Linktree pages always stay on linktr.ee.',
      icon: 'i-lucide-globe'
    },
    {
      title: 'Animated backgrounds and cursors',
      description: 'Rain, snow, fire, sakura, plasma, and more, plus custom cursors and cursor trails. Linktree has neither.',
      icon: 'i-lucide-sparkles'
    },
    {
      title: 'A music player on every plan',
      description: 'Upload your own tracks and they play while people look around, not inside a third-party embed.',
      icon: 'i-lucide-music'
    },
    {
      title: 'Video clips that play on the page',
      description: 'Upload highlights that visitors watch in place, up to 18 on Elite. Linktree embeds video from other sites.',
      icon: 'i-lucide-clapperboard'
    },
    {
      title: 'No branding for €6.99, once',
      description: 'Pro removes the mychar.gg branding for a single payment. On Linktree that takes Pro at $12 a month.',
      icon: 'i-lucide-badge-euro'
    },
    {
      title: 'A page people can talk on',
      description: 'Visitor comments, linked friends, achievements, a game library, and a setup section are ready to add.',
      icon: 'i-lucide-message-circle'
    }
  ],
  strengths: [
    {
      title: 'Selling from your bio',
      description: 'Linktree sells digital products and courses on every plan, with an affiliate shop and Shopify connection. mychar.gg has no store.'
    },
    {
      title: 'Marketing tools',
      description: 'Email and SMS signups, mailing list sync, Instagram auto-replies, link scheduling, and ad pixels go well beyond a bio page.'
    },
    {
      title: 'A big app catalog',
      description: 'Around 60 marketplace apps connect music, video, payments, and community tools, and Premium adds all-time analytics with export.'
    },
    {
      title: 'Recognition and mobile apps',
      description: 'Audiences know linktr.ee links on sight, and native iOS and Android apps let you edit on the go. mychar.gg runs in the browser.'
    }
  ],
  sections: [
    {
      title: 'Why people look for a Linktree alternative',
      paragraphs: [
        'Linktree made the link in bio a standard, and it does that job reliably. The usual reasons to look elsewhere are about how the page looks and what it costs. On the free plan, most pages look alike: a stack of buttons on a theme, with Linktree branding at the bottom. The things that make a page feel like yours, such as video backgrounds and branding removal, sit behind Pro at $12 a month billed yearly.',
        'There are also things Linktree does not do at all. You cannot put a Linktree page on your own domain, there are no animated backgrounds or custom cursors, and music or video only appears through embeds from other services. For creators whose brand is visual, that is the gap.'
      ]
    },
    {
      title: 'How mychar.gg is different',
      paragraphs: [
        'mychar.gg treats the page as something you design, not a list you fill in. The [editor](/docs/customization) is your actual page: you edit your name and bio where they appear, drag sections, links, and widgets into place, and pick layouts, fonts, colors, backgrounds, effects, and cursors while everything updates live. You can start from a community [skin](/skins) and change any detail.',
        'Your content lives on the page too. A built-in music player plays your own tracks, video clips play in place, and YouTube and Twitch sections keep your latest uploads and live status current. Analytics are included on every plan. Pricing is the other big difference: Pro is €6.99 and Elite €12.99, both paid once, and a custom domain is a €2.99 per month add-on that works even on Free. Our post on the [best Linktree alternatives](/blog/best-linktree-alternatives-2026) covers other options as well.'
      ]
    }
  ],
  switchSteps: [
    { title: 'Claim your username', description: 'Sign up free at [mychar.gg/signup](/signup). Your page lives at mychar.gg/yourname.' },
    { title: 'Copy your links over', description: 'Add your platforms from the list of about 35 built-in link types, which are unlimited on every plan. Links to any other URL, like a shop or a newsletter, start on Pro.' },
    { title: 'Design the page', description: 'Pick a layout, fonts, colors, and a background, or apply a community skin, and arrange everything by dragging. The [builder guide](/docs/customization) walks through each option.' },
    { title: 'Add what Linktree could only link to', description: 'Upload a track to the music player, add video clips, and connect YouTube or Twitch so they update themselves.' },
    { title: 'Swap the link in your bios', description: 'Replace your linktr.ee URL on Instagram, TikTok, YouTube, and everywhere else. The [sharing guide](/docs/getting-started/sharing) covers each platform.' }
  ],
  faq: [
    {
      label: 'Is mychar.gg free like Linktree?',
      content: 'Yes. The Free plan is free forever with no credit card, and it includes unlimited platform links, a music player, a Discord section, and analytics. Pro (€6.99) and Elite (€12.99) are one-time upgrades, not subscriptions.'
    },
    {
      label: 'Can I use my own domain instead of linktr.ee?',
      content: 'Linktree does not offer custom domains. On mychar.gg, the custom domain add-on is €2.99 per month on any plan, includes SSL, and keeps your mychar.gg/yourname link working. See the [custom domain guide](/docs/profile/custom-domain).'
    },
    {
      label: 'Can I sell products like on Linktree?',
      content: 'Not directly. mychar.gg has no store or checkout. You can link to your shop, Ko-fi, Patreon, or PayPal, but if selling from your bio is the main point, Linktree or a commerce tool fits better.'
    },
    {
      label: 'Does mychar.gg have analytics like Linktree?',
      content: 'Yes, on every plan: views, clicks, and top links. Pro adds live visitors, countries, click rate, devices, and referrers, and Elite keeps lifetime history. See [statistics](/docs/statistics).'
    },
    {
      label: 'Will my links work the same way?',
      content: 'Yes. Every link opens the same destination. You can also reorder them by dragging, give custom links your own label and icon on Pro, and show a Discord invite as a live server card on Elite.'
    }
  ]
}

export default linktree
